import React, { useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Colors } from '../config/default';
import { pullBackDocument } from '../services/services';
import { useToast } from '../toast/toastContext';
import Prompt from './prompt';
import { documentTypes, documentStatusText } from '../constants/appConstants';

const { DELIVERY_TICKET } = documentTypes;
const { APPROVED } = documentStatusText;

/**
 * "Edit ticket" on the invoice's attached-delivery-ticket view.
 *
 * A delivery ticket attached to an invoice drops out of the documents list as an
 * ordinary row (it reads "Attached" there, and the pull-back icon stays off for
 * it), so this is the way back to it. It calls the same pull back as the list:
 * Tier B returns the ticket to To Review and leaves it attached to the invoice.
 * Then the ticket opens on the canvas to be corrected and approved again.
 *
 * Shown only to a role with the pullBackDocument permission — the same lookup
 * iconColumn makes.
 *
 * The ticket's own status is NOT on the attachment reference the invoice carries
 * ({id, documentType, s3OriginalKeys}). It does not need to be: only an approved
 * ticket can be attached, and once one has been pulled back it is opened from
 * here immediately. If Tier B ever refuses (the ticket is no longer approved), its
 * message is shown as the toast. A reference that does carry a status other than
 * Approved hides the action outright.
 */
const EditAttachedTicket = ({ tickets, onOpenDocument }) => {
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const { showToast } = useToast();
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);

  const rolePermissions = userDetails?.roleConfigurations?.permissionConfiguration;
  const pullBackPermission = rolePermissions?.[userDetails?.role]?.pullBackDocument;

  const editable = (Array.isArray(tickets) ? tickets : []).filter(
    (ticket) =>
      ticket?.id &&
      (!ticket?.documentType || ticket.documentType === DELIVERY_TICKET) &&
      (!ticket?.documentStatus || ticket.documentStatus === APPROVED)
  );
  if (!pullBackPermission || editable.length === 0) return null;

  const pullBack = async () => {
    if (!pending) return;
    setLoading(true);
    const result = await pullBackDocument({
      documentIds: [pending],
      companyId: userDetails?.company?._id
    });
    setLoading(false);
    const ticketId = pending;
    setPending(null);
    if (result?.status === 200) {
      showToast(result?.data?.metadata?.message, 'success');
      onOpenDocument?.(ticketId);
    } else {
      showToast(
        result?.response?.data?.metadata?.message || result?.data?.metadata?.message,
        'error'
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      {editable.map((ticket, index) => (
        <Button
          key={ticket.id}
          variant="outlined"
          size="small"
          disabled={loading}
          startIcon={
            loading && pending === ticket.id ? (
              <CircularProgress size={14} sx={{ color: Colors.TOURQUISE }} />
            ) : (
              <EditOutlined sx={{ fontSize: '16px' }} />
            )
          }
          onClick={() => setPending(ticket.id)}
          sx={{
            textTransform: 'none',
            color: Colors.TOURQUISE,
            borderColor: Colors.TOURQUISE,
            borderRadius: '50px',
            height: '2rem'
          }}>
          {editable.length > 1 ? `Edit ticket ${index + 1}` : 'Edit ticket'}
        </Button>
      ))}
      <Prompt
        title="Edit delivery ticket"
        description="The ticket goes back to To Review so it can be corrected and approved again. It stays attached to this invoice."
        leftButton="Cancel"
        rightButton="Edit ticket"
        open={Boolean(pending)}
        setOpen={(open) => {
          if (!open) setPending(null);
        }}
        handleClick={pullBack}
        loading={loading}
        opacity={true}
      />
    </Box>
  );
};

EditAttachedTicket.propTypes = {
  onOpenDocument: PropTypes.func,
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      documentType: PropTypes.string,
      documentStatus: PropTypes.string
    })
  )
};

export default EditAttachedTicket;
